package no.vareflow.app.report

/**
 * Writes a [LaidOutReport] as an .xlsx workbook (Office Open XML, one worksheet per sheet).
 * Numbers stay numbers (Excel can sum and filter them); texts are inline strings.
 */
object XlsxWriter {
    private const val S_TITLE = 1
    private const val S_SUBTITLE = 2
    private const val S_HEADING = 3
    private const val S_SUBHEADING = 4
    private const val S_HEADER = 5
    private const val S_HEADER_NUM = 6
    private const val S_MONEY = 7
    private const val S_COUNT = 8
    private const val S_SUM_TEXT = 9
    private const val S_SUM_MONEY = 10
    private const val S_SUM_COUNT = 11
    private const val S_QTY = 12
    private const val S_SUM_TEXT_STRONG = 13
    private const val S_SUM_MONEY_STRONG = 14
    private const val S_SUM_COUNT_STRONG = 15

    fun write(report: LaidOutReport): ByteArray {
        val sheets = report.sheets.ifEmpty { listOf(ReportSheet(if (report.language == no.vareflow.app.domain.AppLanguage.EN) "Stock count" else "Varetelling", emptyList(), emptyList())) }
        val entries = mutableListOf<Pair<String, ByteArray>>()
        val logo = report.logo
        entries += "[Content_Types].xml" to contentTypes(sheets.size, logo != null).encodeToByteArray()
        entries += "_rels/.rels" to ROOT_RELS.encodeToByteArray()
        entries += "docProps/core.xml" to core(report.title).encodeToByteArray()
        entries += "xl/workbook.xml" to workbook(sheets).encodeToByteArray()
        entries += "xl/_rels/workbook.xml.rels" to workbookRels(sheets.size).encodeToByteArray()
        entries += "xl/styles.xml" to STYLES.encodeToByteArray()
        sheets.forEachIndexed { i, sheet -> entries += "xl/worksheets/sheet${i + 1}.xml" to sheetXml(report, sheet).encodeToByteArray() }
        if (logo != null) {
            entries += "xl/media/logo.jpeg" to logo.bytes
            sheets.forEachIndexed { i, sheet ->
                val n = i + 1
                entries += "xl/worksheets/_rels/sheet$n.xml.rels" to (XML_HEAD + "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
                    "<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing\" Target=\"../drawings/drawing$n.xml\"/></Relationships>").encodeToByteArray()
                entries += "xl/drawings/_rels/drawing$n.xml.rels" to (XML_HEAD + "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
                    "<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/image\" Target=\"../media/logo.jpeg\"/></Relationships>").encodeToByteArray()
                entries += "xl/drawings/drawing$n.xml" to drawingXml(logo, sheet).encodeToByteArray()
            }
        }
        return ZipStore.write(entries)
    }

    private fun sheetXml(report: LaidOutReport, sheet: ReportSheet): String {
        val sb = StringBuilder()
        var row = 0
        fun cellRef(col: Int, r: Int): String = columnName(col) + r
        fun text(col: Int, value: String, style: Int) =
            "<c r=\"${cellRef(col, row)}\" t=\"inlineStr\" s=\"$style\"><is><t xml:space=\"preserve\">${esc(value)}</t></is></c>"
        fun number(col: Int, value: Number, style: Int) = "<c r=\"${cellRef(col, row)}\" s=\"$style\"><v>${numberText(value)}</v></c>"
        fun line(cells: String, height: Double? = null) {
            sb.append("<row r=\"$row\"").append(if (height != null) " ht=\"$height\" customHeight=\"1\"" else "").append('>').append(cells).append("</row>")
        }

        row = 0
        if (report.companyName.isNotBlank()) { row++; line(text(0, report.companyName, S_HEADING), 20.0) }
        row++; line(text(0, report.title, S_TITLE), 24.0)
        row++; line(text(0, report.subtitle, S_SUBTITLE))
        row++
        // Room for the logo next to the title rows.
        if (report.logo != null && report.companyName.isBlank()) { row++; line("") }
        var columns = sheet.columns
        sheet.rows.forEach { r ->
            row++
            when (r) {
                is ReportRow.Heading -> line(text(0, r.text, S_HEADING), 20.0)
                is ReportRow.SubHeading -> line(text(0, r.text, S_SUBHEADING))
                is ReportRow.Header -> {
                    columns = r.columns
                    line(r.columns.mapIndexed { i, c -> text(i, c.title, if (c.kind == CellKind.TEXT) S_HEADER else S_HEADER_NUM) }.joinToString(""))
                }
                is ReportRow.Item -> line(r.cells.mapIndexed { i, v ->
                    when (v) {
                        is Number -> number(i, v, when (columns.getOrNull(i)?.kind) {
                            CellKind.MONEY -> S_MONEY
                            CellKind.COUNT -> S_COUNT
                            else -> S_QTY
                        })
                        else -> if (v.toString().isEmpty()) "" else text(i, v.toString(), 0)
                    }
                }.joinToString(""))
                is ReportRow.Sum -> line(r.cells.mapIndexed { i, v ->
                    when (v) {
                        null -> "<c r=\"${cellRef(i, row)}\" s=\"${if (r.strong) S_SUM_TEXT_STRONG else S_SUM_TEXT}\"/>"
                        is Number -> number(i, v, when (columns.getOrNull(i)?.kind) {
                            CellKind.COUNT -> if (r.strong) S_SUM_COUNT_STRONG else S_SUM_COUNT
                            else -> if (r.strong) S_SUM_MONEY_STRONG else S_SUM_MONEY
                        })
                        else -> text(i, v.toString(), if (r.strong) S_SUM_TEXT_STRONG else S_SUM_TEXT)
                    }
                }.joinToString(""))
                ReportRow.Blank -> line("")
            }
        }
        // Column widths: the widest use of each column on this sheet.
        val widths = mutableMapOf<Int, Int>()
        (listOf(sheet.columns) + sheet.rows.filterIsInstance<ReportRow.Header>().map { it.columns }).forEach { cols ->
            cols.forEachIndexed { i, c -> widths[i] = maxOf(widths[i] ?: 0, c.width) }
        }
        val cols = widths.entries.sortedBy { it.key }.joinToString("") { (i, w) -> "<col min=\"${i + 1}\" max=\"${i + 1}\" width=\"${w + 2}\" customWidth=\"1\"/>" }
        return "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
            "<worksheet xmlns=\"http://schemas.openxmlformats.org/spreadsheetml/2006/main\" xmlns:r=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships\">" +
            "<sheetViews><sheetView workbookViewId=\"0\" showGridLines=\"0\"/></sheetViews>" +
            "<sheetFormatPr defaultRowHeight=\"16\"/>" +
            (if (cols.isNotEmpty()) "<cols>$cols</cols>" else "") +
            "<sheetData>$sb</sheetData>" +
            "<pageMargins left=\"0.5\" right=\"0.5\" top=\"0.6\" bottom=\"0.6\" header=\"0.3\" footer=\"0.3\"/>" +
            "<pageSetup paperSize=\"9\" orientation=\"portrait\" fitToWidth=\"1\" fitToHeight=\"0\"/>" +
            (if (report.logo != null) "<drawing r:id=\"rId1\"/>" else "") +
            "</worksheet>"
    }

    private const val XML_HEAD = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>"
    private const val EMU_PER_INCH = 914400L

    /** The logo in the top right of the sheet's title rows (one-cell anchor, fixed size). */
    private fun drawingXml(logo: JpegInfo, sheet: ReportSheet): String {
        var heightIn = 0.62
        var widthIn = heightIn * logo.width / logo.height
        if (widthIn > 2.3) { widthIn = 2.3; heightIn = widthIn * logo.height / logo.width }
        val col = (sheet.columns.size - 2).coerceAtLeast(2)
        val cx = (widthIn * EMU_PER_INCH).toLong()
        val cy = (heightIn * EMU_PER_INCH).toLong()
        return XML_HEAD +
            "<xdr:wsDr xmlns:xdr=\"http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing\" xmlns:a=\"http://schemas.openxmlformats.org/drawingml/2006/main\" xmlns:r=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships\">" +
            "<xdr:oneCellAnchor><xdr:from><xdr:col>$col</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>38100</xdr:rowOff></xdr:from>" +
            "<xdr:ext cx=\"$cx\" cy=\"$cy\"/>" +
            "<xdr:pic><xdr:nvPicPr><xdr:cNvPr id=\"2\" name=\"Logo\"/><xdr:cNvPicPr><a:picLocks noChangeAspect=\"1\"/></xdr:cNvPicPr></xdr:nvPicPr>" +
            "<xdr:blipFill><a:blip r:embed=\"rId1\"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>" +
            "<xdr:spPr><a:xfrm><a:off x=\"0\" y=\"0\"/><a:ext cx=\"$cx\" cy=\"$cy\"/></a:xfrm><a:prstGeom prst=\"rect\"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic>" +
            "<xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>"
    }

    internal fun columnName(index: Int): String {
        var n = index + 1
        val sb = StringBuilder()
        while (n > 0) { val m = (n - 1) % 26; sb.insert(0, ('A' + m)); n = (n - 1) / 26 }
        return sb.toString()
    }

    private fun numberText(value: Number): String {
        val d = value.toDouble()
        if (d == kotlin.math.floor(d) && kotlin.math.abs(d) < 1e15) return d.toLong().toString()
        // Round away float noise (0.1 * 3), keep up to 6 decimals.
        val rounded = kotlin.math.round(d * 1_000_000) / 1_000_000
        return rounded.toString()
    }

    private fun esc(s: String) = buildString(s.length) {
        for (c in s) when {
            c == '&' -> append("&amp;")
            c == '<' -> append("&lt;")
            c == '>' -> append("&gt;")
            c == '"' -> append("&quot;")
            c.code < 0x20 && c != '\t' && c != '\n' && c != '\r' -> Unit
            else -> append(c)
        }
    }

    private fun contentTypes(sheetCount: Int, withLogo: Boolean) = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<Types xmlns=\"http://schemas.openxmlformats.org/package/2006/content-types\">" +
        "<Default Extension=\"rels\" ContentType=\"application/vnd.openxmlformats-package.relationships+xml\"/>" +
        "<Default Extension=\"xml\" ContentType=\"application/xml\"/>" +
        (if (withLogo) "<Default Extension=\"jpeg\" ContentType=\"image/jpeg\"/>" +
            (1..sheetCount).joinToString("") { "<Override PartName=\"/xl/drawings/drawing$it.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.drawing+xml\"/>" } else "") +
        "<Override PartName=\"/xl/workbook.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml\"/>" +
        "<Override PartName=\"/xl/styles.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml\"/>" +
        "<Override PartName=\"/docProps/core.xml\" ContentType=\"application/vnd.openxmlformats-package.core-properties+xml\"/>" +
        (1..sheetCount).joinToString("") { "<Override PartName=\"/xl/worksheets/sheet$it.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml\"/>" } +
        "</Types>"

    private const val ROOT_RELS = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
        "<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument\" Target=\"xl/workbook.xml\"/>" +
        "<Relationship Id=\"rId2\" Type=\"http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties\" Target=\"docProps/core.xml\"/>" +
        "</Relationships>"

    private fun core(title: String) = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<cp:coreProperties xmlns:cp=\"http://schemas.openxmlformats.org/package/2006/metadata/core-properties\" xmlns:dc=\"http://purl.org/dc/elements/1.1/\">" +
        "<dc:title>${esc(title)}</dc:title><dc:creator>Vareflow</dc:creator></cp:coreProperties>"

    private fun workbook(sheets: List<ReportSheet>) = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<workbook xmlns=\"http://schemas.openxmlformats.org/spreadsheetml/2006/main\" xmlns:r=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships\">" +
        "<sheets>" + sheets.mapIndexed { i, s -> "<sheet name=\"${esc(s.name)}\" sheetId=\"${i + 1}\" r:id=\"rId${i + 1}\"/>" }.joinToString("") + "</sheets></workbook>"

    private fun workbookRels(sheetCount: Int) = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
        (1..sheetCount).joinToString("") { "<Relationship Id=\"rId$it\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet\" Target=\"worksheets/sheet$it.xml\"/>" } +
        "<Relationship Id=\"rId${sheetCount + 1}\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles\" Target=\"styles.xml\"/>" +
        "</Relationships>"

    // Fonts: 0 normal, 1 bold, 2 title, 3 grey, 4 heading, 5 bold italic. Fills: 2 = light grey header.
    // Borders: 1 = thin bottom (header), 2 = thin top (sums).
    private const val STYLES = "<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>" +
        "<styleSheet xmlns=\"http://schemas.openxmlformats.org/spreadsheetml/2006/main\">" +
        "<numFmts count=\"1\"><numFmt numFmtId=\"164\" formatCode=\"#,##0.00\\ &quot;kr&quot;\"/></numFmts>" +
        "<fonts count=\"6\">" +
        "<font><sz val=\"11\"/><name val=\"Calibri\"/></font>" +
        "<font><b/><sz val=\"11\"/><name val=\"Calibri\"/></font>" +
        "<font><b/><sz val=\"16\"/><color rgb=\"FF1E2A35\"/><name val=\"Calibri\"/></font>" +
        "<font><sz val=\"10\"/><color rgb=\"FF6B7783\"/><name val=\"Calibri\"/></font>" +
        "<font><b/><sz val=\"13\"/><color rgb=\"FF2F6E9E\"/><name val=\"Calibri\"/></font>" +
        "<font><b/><i/><sz val=\"11\"/><color rgb=\"FF5E6B77\"/><name val=\"Calibri\"/></font>" +
        "</fonts>" +
        "<fills count=\"3\"><fill><patternFill patternType=\"none\"/></fill><fill><patternFill patternType=\"gray125\"/></fill>" +
        "<fill><patternFill patternType=\"solid\"><fgColor rgb=\"FFEEF1F4\"/><bgColor indexed=\"64\"/></patternFill></fill></fills>" +
        "<borders count=\"3\"><border><left/><right/><top/><bottom/><diagonal/></border>" +
        "<border><left/><right/><top/><bottom style=\"thin\"><color rgb=\"FF9AA5B1\"/></bottom><diagonal/></border>" +
        "<border><left/><right/><top style=\"thin\"><color rgb=\"FF9AA5B1\"/></top><bottom/><diagonal/></border></borders>" +
        "<cellStyleXfs count=\"1\"><xf numFmtId=\"0\" fontId=\"0\" fillId=\"0\" borderId=\"0\"/></cellStyleXfs>" +
        "<cellXfs count=\"16\">" +
        "<xf numFmtId=\"0\" fontId=\"0\" fillId=\"0\" borderId=\"0\" xfId=\"0\"/>" + // 0 normal
        "<xf numFmtId=\"0\" fontId=\"2\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyFont=\"1\"/>" + // 1 title
        "<xf numFmtId=\"0\" fontId=\"3\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyFont=\"1\"/>" + // 2 subtitle
        "<xf numFmtId=\"0\" fontId=\"4\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyFont=\"1\"/>" + // 3 heading
        "<xf numFmtId=\"0\" fontId=\"5\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyFont=\"1\"/>" + // 4 subheading
        "<xf numFmtId=\"0\" fontId=\"1\" fillId=\"2\" borderId=\"1\" xfId=\"0\" applyFont=\"1\" applyFill=\"1\" applyBorder=\"1\"/>" + // 5 header
        "<xf numFmtId=\"0\" fontId=\"1\" fillId=\"2\" borderId=\"1\" xfId=\"0\" applyFont=\"1\" applyFill=\"1\" applyBorder=\"1\" applyAlignment=\"1\"><alignment horizontal=\"right\"/></xf>" + // 6 header num
        "<xf numFmtId=\"164\" fontId=\"0\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyNumberFormat=\"1\"/>" + // 7 money
        "<xf numFmtId=\"1\" fontId=\"0\" fillId=\"0\" borderId=\"0\" xfId=\"0\" applyNumberFormat=\"1\"/>" + // 8 count
        "<xf numFmtId=\"0\" fontId=\"5\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\"/>" + // 9 sum text
        "<xf numFmtId=\"164\" fontId=\"5\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\" applyNumberFormat=\"1\"/>" + // 10 sum money
        "<xf numFmtId=\"1\" fontId=\"5\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\" applyNumberFormat=\"1\"/>" + // 11 sum count
        "<xf numFmtId=\"0\" fontId=\"0\" fillId=\"0\" borderId=\"0\" xfId=\"0\"/>" + // 12 quantity (General)
        "<xf numFmtId=\"0\" fontId=\"1\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\"/>" + // 13 strong sum text
        "<xf numFmtId=\"164\" fontId=\"1\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\" applyNumberFormat=\"1\"/>" + // 14 strong sum money
        "<xf numFmtId=\"1\" fontId=\"1\" fillId=\"0\" borderId=\"2\" xfId=\"0\" applyFont=\"1\" applyBorder=\"1\" applyNumberFormat=\"1\"/>" + // 15 strong sum count
        "</cellXfs>" +
        "<cellStyles count=\"1\"><cellStyle name=\"Normal\" xfId=\"0\" builtinId=\"0\"/></cellStyles>" +
        "</styleSheet>"
}
